import { watch } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import { useRoute } from '#imports'
import type { UserPreferencesPayload } from '~/composables/ui/useUserPreferences'

type AutoStartDeps = {
  userPreferencesReady: Ref<boolean>
  loggedIn: Ref<boolean>
  roles: Ref<string[]>
  canEditOrganigramOnCurrentView: Ref<boolean>
  isNoticeUpToDate: Ref<boolean>
  isEnvironmentNoticeOpen: Ref<boolean>
  isSeasonalWelcomeOpen: Ref<boolean>
  isWelcomeResolved: Ref<boolean>
  prefs: Ref<UserPreferencesPayload>
  hasHydrated: Ref<boolean>
  launchBlockerReason: ComputedRef<string | null>
  toursReady: ComputedRef<boolean>
  disabledTourSignature: ComputedRef<string>
  syncStateFromStorageAndApi: () => void
  maybeStartTourOnCurrentRoute: () => Promise<void>
}

/**
 * Déclenche `maybeStartTourOnCurrentRoute` dès que l’état d’hydratation, la route,
 * ou l’un des "bloqueurs" de lancement (charte, popups, avatar…) change.
 * `maybeStartTourOnCurrentRoute` porte elle-même la protection anti-concurrence
 * (voir `useOnboardingDriverRuntime`), donc ces deux déclencheurs peuvent coexister sans risque.
 */
export function useOnboardingAutoStart(deps: AutoStartDeps): void {
  const route = useRoute()

  function playwrightGuideDisabled(): boolean {
    if (!import.meta.client) {
      return false
    }
    try {
      if (window.localStorage.getItem('monorga.playwright.disableOnboarding') === '1') {
        return true
      }
    } catch {
      return false
    }
    return new URLSearchParams(window.location.search).get('pw') === '1'
  }

  watch(
    () => ({
      ready: deps.userPreferencesReady.value,
      loggedIn: deps.loggedIn.value,
      onboardingSignature: JSON.stringify(deps.prefs.value.onboarding?.state ?? {}),
      toursReady: deps.toursReady.value,
      disabledTourSignature: deps.disabledTourSignature.value,
    }),
    ({ ready, loggedIn: isLoggedIn }) => {
      if (!ready || import.meta.server) {
        return
      }
      deps.syncStateFromStorageAndApi()
      if (!deps.hasHydrated.value) {
        deps.hasHydrated.value = true
      }
      if (!isLoggedIn || deps.launchBlockerReason.value !== null || playwrightGuideDisabled()) {
        return
      }
      void deps.maybeStartTourOnCurrentRoute()
    },
    { immediate: true }
  )

  watch(
    () => ({
      path: route.path,
      loggedIn: deps.loggedIn.value,
      roles: [...deps.roles.value].sort().join('|'),
      canEdit: deps.canEditOrganigramOnCurrentView.value,
      hydrationReady: deps.hasHydrated.value,
      noticeReady: deps.isNoticeUpToDate.value,
      envNoticeOpen: deps.isEnvironmentNoticeOpen.value,
      seasonalOpen: deps.isSeasonalWelcomeOpen.value,
      welcomeResolved: deps.isWelcomeResolved.value,
      toursReady: deps.toursReady.value,
      disabledTourSignature: deps.disabledTourSignature.value,
    }),
    async () => {
      if (!deps.hasHydrated.value || playwrightGuideDisabled()) {
        return
      }
      await deps.maybeStartTourOnCurrentRoute()
    },
    { immediate: true, flush: 'post' }
  )
}
